import * as React from 'react';

import { showToast } from '@opendoor/bricks/complex/Toast';
import { dismissToast } from '@opendoor/bricks/complex/Toast/Toast';
import { Button } from '@opendoor/bricks/core';

import {
  NavigationContainer,
  NavigationCTAContainer,
  NovoButton,
  PreviousLink,
} from 'src/components/layouts/NavigationLayout';
import { FlowQuestion } from 'src/lib/flow/FlowQuestion';
import { AnswerValidation, checkValidationForActiveQuestions } from 'src/lib/flow/validation';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { getCurrentSlug } from 'src/store/router/selectors';
import { getAnswers } from 'src/store/sellerFlow/selectors';
import { changeAnswers } from 'src/store/sellerInput';
import { clearValidation, showValidation } from 'src/store/ui';

import { submitAndGoToNextPage } from '../../../store/ui';

const SkipButton = () => {
  const currentSlug = useAppSelector(getCurrentSlug);
  const showSkipButton = useAppSelector((state) => !!state.ui.page?.showSkipButton);
  const dispatch = useAppDispatch();
  const onClick = () => {
    dispatch(submitAndGoToNextPage());
  };

  if (!showSkipButton) {
    return null;
  }

  return (
    <Button
      aria-label={null}
      analyticsName={`reception-fe-${currentSlug}-skip`}
      type="button"
      onClick={onClick}
      variant="secondary"
    >
      Skip
    </Button>
  );
};

const showToastValidation = (
  questions: Array<FlowQuestion>,
  validations: AnswerValidation,
): boolean => {
  if (questions.length > 1) {
    return false;
  }

  const type = questions[0].type;
  if (type !== 'radio_single_select' && type !== 'multi_select' && type !== 'image_single_select') {
    return false;
  }

  return (
    validations.valid.length == 0 &&
    validations.invalid.length == 1 &&
    !validations.invalid[0].result.isValid &&
    validations.invalid[0].result.type === 'required'
  );
};

const NextButton = () => {
  const page = useAppSelector((state) => state.ui.page);
  const atEnd = useAppSelector((state) => state.flow_ng.atEnd);
  const currentSlug = useAppSelector(getCurrentSlug);
  const loading = useAppSelector((state) => state.ui.pageLoading);
  const questions = useAppSelector((state) => state.ui.page?.questions);
  const answers = useAppSelector(getAnswers);
  const uiShowValidation = useAppSelector((state) => state.ui.showValidation);
  const dispatch = useAppDispatch();

  const [doFocus, setDoFocus] = React.useState(false);

  let nextButtonText = atEnd ? 'Submit & Finish' : 'Next';
  if (page?.nextText) {
    nextButtonText = page?.nextText;
  }

  // clear validation display when navigating to a new page
  React.useEffect(() => {
    dispatch(clearValidation);
    dismissToast('error-toast');
  }, [currentSlug, dispatch]);

  // focus on invalid elements everytime we do validation
  React.useEffect(() => {
    if (uiShowValidation && doFocus) {
      const el = document.querySelector<HTMLLabelElement>('label[data-has-error="true"]');
      if (el) {
        el.focus();
        el.scrollIntoView();
      }
      setDoFocus(false);
    }
  }, [uiShowValidation, doFocus]);

  const handlePresubmit = (event: React.MouseEvent) => {
    // do nothing while loading, but also prevent double submission
    if (loading) {
      event.preventDefault();
      return;
    }

    // figure out what kind of error messaging to show
    if (questions) {
      const validations = checkValidationForActiveQuestions(questions, answers);
      if (
        validations.invalid.length !== 0 ||
        validations.customValidatorErrorMessages.length !== 0
      ) {
        event.preventDefault();

        setDoFocus(true);

        // if we only have a single question and its invalid, show some toast
        if (showToastValidation(questions, validations)) {
          showToast(
            'error',
            'Please make a selection to move forward.',
            'reception-fe-form-invalid-toast',
            {
              toastOptions: { toastId: 'error-toast', autoClose: 7000 },
            },
          );
        } else {
          // otherwise we need to have the ui highlight the invalid field(s)
          dispatch(showValidation());
        }
        return;
      }
    }

    // Turn off showing error messages and let the event bubble up
    dispatch(clearValidation());

    // if this page has a secondary action unset any of those answsers since the user chose the primary action
    if (page?.secondaryAction) {
      dispatch(
        changeAnswers(page.secondaryAction.answerSet.map(({ key }) => ({ key, answer: null }))),
      );
    }
  };

  return (
    <NovoButton
      variant="primary"
      onClick={handlePresubmit}
      loading={loading}
      width="auto"
      minWidth={['100px', null, null, '115px']}
      border="1.5px solid"
      borderColor="brand50"
      analyticsName={`reception-fe-${currentSlug}-${nextButtonText}`}
      trackImpression={true}
      aria-label={null}
      type="submit"
      id="od-form-submit"
    >
      {nextButtonText}
    </NovoButton>
  );
};

const SecondaryActionButton = () => {
  const [loading, setLoading] = React.useState(false);
  const page = useAppSelector((state) => state.ui.page);
  const dispatch = useAppDispatch();

  if (!page) {
    return null;
  }

  const { secondaryAction } = page;

  if (secondaryAction) {
    return (
      <Button
        variant="secondary"
        onClick={() => {
          dispatch(changeAnswers(secondaryAction.answerSet));
          setLoading(true);
          dispatch(submitAndGoToNextPage());
        }}
        loading={loading}
        width="auto"
        minWidth={['100px', null, null, '115px']}
        paddingX={['16px', null, '24px', null]}
        analyticsName={`reception-fe-${page.slug}-${secondaryAction.label}`}
        trackImpression={true}
        aria-label={null}
      >
        {secondaryAction.label}
      </Button>
    );
  }

  if (loading) {
    setLoading(false);
  }
  return null;
};

type NavigationProps = {
  showNextButton?: boolean;
};

const Navigation: React.FC<NavigationProps> = ({ showNextButton = true }) => {
  // todo - need to make it so 922+ can be the final full width+padding
  return (
    <NavigationContainer>
      <NavigationCTAContainer>
        <SkipButton />
        {showNextButton ? (
          <>
            <SecondaryActionButton />
            <NextButton />
          </>
        ) : (
          // we still render a button, but we make it not visible, this makes
          // it so the bottom bar maintains the same height as having a button
          // present
          <Button
            analyticsName="reception-fe-hidden-next-button"
            aria-label={null}
            onClick={() => {}}
            visibility="hidden"
          >
            Next
          </Button>
        )}
      </NavigationCTAContainer>
      <PreviousLink />
    </NavigationContainer>
  );
};

export default Navigation;
